import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type React from 'react'
import { STATION_NAV_SECTIONS, type StationNavSectionId } from './stationNavSections'
import { animateWindowScrollTo } from './animateWindowScrollTo'
import styles from './StationNav.module.css'

/** Sections sit under the 62px tab row (`scroll-margin-top` mirror). */
const SCROLL_OFFSET = 62

/**
 * Anchor tab row of the station-page template — Figma 8597:41522
 * (TASK-699, design TASK-584). Each tab is a plain `#hash` anchor
 * (works before hydration on prerendered pages); on click the tab runs
 * the `animateWindowScrollTo` rAF tween — see `handleClick` for why
 * native smooth scrolling cannot be trusted here.
 *
 * Active state is click-driven for now; scroll-spy follows once the
 * section blocks themselves land.
 */
export function StationNav(): React.JSX.Element {
  const { t } = useTranslation('station-page')
  const [activeId, setActiveId] = useState<StationNavSectionId>('overview')

  // Native smooth scrolling (CSS scroll-behavior / scrollIntoView
  // smooth) silently no-ops in browsers with smooth animation disabled
  // — observed live: hash set, page never moved. So when the section
  // block exists the tab takes over: prevent the native jump, run the
  // environment-proof rAF tween, record the hash for shareable URLs.
  // A missing block keeps the native anchor jump as fallback.
  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    id: StationNavSectionId,
  ): void => {
    const target = document.getElementById(id)
    if (target === null) return
    event.preventDefault()
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo({ top, behavior: 'auto' })
    } else {
      animateWindowScrollTo(top)
    }
    history.replaceState(null, '', `#${id}`)
    setActiveId(id)
  }

  return (
    <nav aria-label={t('nav_aria')} className={styles.stationNav}>
      <div className={styles.row}>
        {STATION_NAV_SECTIONS.map(({ id, labelKey }) => (
          <a
            key={id}
            href={`#${id}`}
            aria-current={activeId === id ? 'location' : undefined}
            className={styles.tab}
            onClick={(event) => {
              handleClick(event, id)
            }}
          >
            {t(labelKey)}
          </a>
        ))}
      </div>
    </nav>
  )
}
